import { CSV_CATALOG } from '../generated/csvPaths';
import {
	CSV_INDIVIDUALS_PATH,
	CSV_TEAMS_PATH,
	DEFAULT_YEAR,
} from './quillesConstants';
import { sortIndividuelCategories, sortTeamSeniorSeriesCategories } from './categoryOrder';

const TEAM_FIELDS = [
	'zone', 'championship', 'typeCompetition', 'category', 'teamNumber', 'teamName', 'club',
	'round', 'quilles', 'parties', 'average', 'totalQuilles', 'totalParties', 'totalAverage',
];

const INDIVIDUAL_FIELDS = [
	'zone', 'championship', 'typeCompetition', 'category', 'teamNumber', 'teamName', 'round',
	'licence', 'playerName', 'club', 'licenceLevel', 'licenceCategory', 'quilles', 'parties',
	'average', 'quilles1', 'quilles2', 'quilles3', 'quilles4', 'totalQuilles', 'totalParties',
	'totalAverage',
	/** Optionnel (23e colonne CS12) : ordre sur le carton (1–4). */
	'cartonOrder',
];

const cacheByYear = new Map();
const loadPromises = new Map();

/** CL seniors (hommes/féminines + vétérans / super-vétérans). */
export const SENIOR_LICENCE_CATEGORIES = ['SH', 'SF', 'SV', 'VH'];

export function isSeniorLicenceCategory(category) {
	return SENIOR_LICENCE_CATEGORIES.includes(String(category ?? '').trim().toUpperCase());
}

/** Marqueur CS12 : ligne en erreur, pas une zone géographique. */
function isValidZone(zone) {
	const value = (zone ?? '').trim();
	if (!value) return false;
	return value.toLowerCase() !== '(err)';
}

function getCatalog() {
	return CSV_CATALOG ?? {
		defaultYear: DEFAULT_YEAR,
		years: [DEFAULT_YEAR],
		byYear: {
			[DEFAULT_YEAR]: {
				teams: CSV_TEAMS_PATH,
				individuals: CSV_INDIVIDUALS_PATH,
			},
		},
	};
}

export function getDefaultYear() {
	return getCatalog().defaultYear ?? DEFAULT_YEAR;
}

export function getAvailableYears() {
	return getCatalog().years ?? [DEFAULT_YEAR];
}

export function resolveSeasonYear(requestedYear, availableYears = getAvailableYears()) {
	const year = Number(requestedYear);
	if (availableYears.includes(year)) return year;
	return availableYears[0] ?? getDefaultYear();
}

function getPathsForYear(seasonYear) {
	const year = resolveSeasonYear(seasonYear);
	const paths = getCatalog().byYear?.[year];
	if (!paths?.teams || !paths?.individuals) {
		throw new Error(`Aucun fichier CSV pour l'année ${year}.`);
	}
	return { ...paths, seasonYear: year };
}

export function clearCsvDataCache() {
	cacheByYear.clear();
	loadPromises.clear();
}

function parseCsvLine(line) {
	const cells = [];
	let current = '';
	let inQuotes = false;
	for (let i = 0; i < line.length; i += 1) {
		const ch = line[i];
		if (ch === '"') {
			inQuotes = !inQuotes;
		} else if (ch === ';' && !inQuotes) {
			cells.push(current);
			current = '';
		} else {
			current += ch;
		}
	}
	cells.push(current);
	return cells.map((cell) => cell.trim());
}

function mapRow(fields, cells) {
	const row = {};
	fields.forEach((field, index) => {
		row[field] = cells[index] ?? '';
	});
	return row;
}

async function fetchCsvText(path) {
	const response = await fetch(path);
	if (!response.ok) {
		throw new Error(`Impossible de charger ${path} (HTTP ${response.status})`);
	}
	const buffer = await response.arrayBuffer();
	return new TextDecoder('iso-8859-1').decode(buffer);
}

function parseCartonOrder(value) {
	const n = Number(String(value ?? '').trim());
	return Number.isFinite(n) && n > 0 ? n : null;
}

function parseCsv(text, fields, { trackSourceLine = false } = {}) {
	return text
		.split(/\r?\n/)
		.filter((line) => line.trim())
		.slice(1)
		.map((line, lineIndex) => {
			const row = mapRow(fields, parseCsvLine(line));
			if (trackSourceLine) {
				row._sourceLineIndex = lineIndex;
			}
			return row;
		})
		.filter((row) => isValidZone(row.zone));
}

export async function loadCsvData(seasonYear = getDefaultYear()) {
	const paths = getPathsForYear(seasonYear);
	const { seasonYear: year } = paths;

	if (cacheByYear.has(year)) return cacheByYear.get(year);
	if (loadPromises.has(year)) return loadPromises.get(year);

	const promise = (async () => {
		const [teamsText, individualsText] = await Promise.all([
			fetchCsvText(paths.teams),
			fetchCsvText(paths.individuals),
		]);
		const data = {
			teams: parseCsv(teamsText, TEAM_FIELDS),
			individuals: parseCsv(individualsText, INDIVIDUAL_FIELDS, {
				trackSourceLine: true,
			}),
			seasonYear: year,
			csvPaths: paths,
			years: getAvailableYears(),
		};
		cacheByYear.set(year, data);
		return data;
	})();

	loadPromises.set(year, promise);

	try {
		return await promise;
	} finally {
		loadPromises.delete(year);
	}
}

function sameContext(row, { zone, championship, category }) {
	return row.zone === zone
		&& row.championship === championship
		&& row.category === category;
}

function matchPlayerContext(row, { zone, championship, category }, { allZones = false } = {}) {
	if (row.championship !== championship || row.category !== category) return false;
	if (!allZones && row.zone !== zone) return false;
	return true;
}

function isTeamCompetitionType(typeCompetition) {
	const t = (typeCompetition ?? '').trim().toUpperCase();
	return t === 'EQUIPES' || t === 'EQUIPE';
}

function isIndividuelCompetitionType(typeCompetition) {
	return (typeCompetition ?? '').trim().toUpperCase() === 'INDIVIDUEL';
}

/** Ligne du fichier équipes à prendre en compte (hors INDIVIDUEL — source : fichier individuel). */
export function isEquipesTeamRow(row) {
	return !isIndividuelCompetitionType(row?.typeCompetition);
}

function competitionTypeFromRows(rows) {
	if (rows.some((row) => isTeamCompetitionType(row.typeCompetition))) return 'EQUIPES';
	if (rows.some((row) => isIndividuelCompetitionType(row.typeCompetition))) return 'INDIVIDUEL';
	return (rows[0]?.typeCompetition ?? '').trim();
}

/** EQUIPES/EQUIPE vs INDIVIDUEL pour un championnat (équipes, puis individuel si absent). */
export function getCompetitionType(teams, zone, championship, individuals = null) {
	const teamRows = teams.filter(
		(row) => row.zone === zone
			&& row.championship === championship
			&& isEquipesTeamRow(row),
	);
	const teamType = competitionTypeFromRows(teamRows);
	if (teamType) return teamType;

	if (individuals) {
		const individualRows = individuals.filter(
			(row) => row.zone === zone && row.championship === championship,
		);
		return competitionTypeFromRows(individualRows);
	}

	return '';
}

export function isIndividuelChampionship(teams, zone, championship, individuals = null) {
	return getCompetitionType(teams, zone, championship, individuals) === 'INDIVIDUEL';
}

export function getZones(teams, individuals = null) {
	const zones = new Set(teams.map((row) => row.zone).filter(isValidZone));
	if (individuals) {
		individuals.forEach((row) => {
			if (isValidZone(row.zone)) zones.add(row.zone);
		});
	}
	return [...zones].sort((a, b) => a.localeCompare(b, 'fr'));
}

export function getChampionships(teams, zone, individuals = null) {
	const championships = new Set(
		teams
			.filter((row) => row.zone === zone && isEquipesTeamRow(row))
			.map((row) => row.championship)
			.filter(Boolean),
	);
	if (individuals) {
		individuals.forEach((row) => {
			if (row.zone === zone && row.championship) championships.add(row.championship);
		});
	}
	return [...championships].sort((a, b) => a.localeCompare(b, 'fr'));
}

export function getCategories(teams, zone, championship, individuals = null) {
	const rows = teams.filter(
		(row) => row.zone === zone
			&& row.championship === championship
			&& isEquipesTeamRow(row),
	);

	if (isIndividuelChampionship(teams, zone, championship, individuals) && individuals) {
		const categories = [...new Set(
			individuals
				.filter(
					(row) => row.zone === zone
						&& row.championship === championship
						&& isIndividuelCompetitionType(row.typeCompetition),
				)
				.map((row) => row.category)
				.filter(Boolean),
		)];
		return sortIndividuelCategories(categories);
	}

	const categories = [...new Set(rows.map((row) => row.category).filter(Boolean))];
	const isTeamChampionship = rows.some((row) => isTeamCompetitionType(row.typeCompetition));
	if (isTeamChampionship) {
		return sortTeamSeniorSeriesCategories(categories);
	}
	return categories.sort((a, b) => a.localeCompare(b, 'fr'));
}

/** Totaux à partir des manches du contexte (pas les champs CSV cumulés multi-compétitions). */
function totalsFromRoundRows(rounds) {
	if (!rounds.length) {
		return { totalQuilles: 0, totalParties: 0, totalAverage: 0 };
	}
	const totalQuilles = rounds.reduce((sum, row) => sum + Number(row.quilles ?? 0), 0);
	const totalParties = rounds.reduce((sum, row) => sum + Number(row.parties ?? 0), 0);
	return {
		totalQuilles,
		totalParties,
		totalAverage: totalParties > 0 ? totalQuilles / totalParties : 0,
	};
}

function groupTeamRounds(rows) {
	const rounds = rows
		.map((row) => ({
			round: row.round,
			quilles: Number(row.quilles),
			parties: Number(row.parties),
			average: Number(row.average),
		}))
		.sort((a, b) => a.round.localeCompare(b.round, undefined, { numeric: true }));

	const last = rounds[rounds.length - 1];
	const previous = rounds[rounds.length - 2];
	const totals = totalsFromRoundRows(rounds);

	return {
		rounds,
		quillesN1: previous ? previous.quilles : 0,
		quillesN: last ? last.quilles : 0,
		...totals,
	};
}

function summaryFromTeamPartyTotals(totals) {
	if (!totals.length) {
		return { totalParties: 0, average: null };
	}
	const sum = totals.reduce((acc, value) => acc + value, 0);
	return {
		totalParties: totals.length,
		average: sum / totals.length,
	};
}

/** Totaux P1…P4 d’équipe (somme des joueurs), toutes manches ou une seule. */
export function computeTeamPartyStats(individuals, filters, teamNumber, round = null) {
	const perRound = new Map();

	individuals
		.filter(
			(row) => sameContext(row, filters)
				&& row.teamNumber === teamNumber
				&& (!round || row.round === round),
		)
		.forEach((row) => {
			if (!perRound.has(row.round)) {
				perRound.set(row.round, [0, 0, 0, 0]);
			}
			const sums = perRound.get(row.round);
			sums[0] += Number(row.quilles1);
			sums[1] += Number(row.quilles2);
			sums[2] += Number(row.quilles3);
			sums[3] += Number(row.quilles4);
		});

	if (!perRound.size) return null;

	const totals = [];
	perRound.forEach((sums) => {
		sums.forEach((value) => {
			if (value > 0) totals.push(value);
		});
	});

	return summaryFromTeamPartyTotals(totals);
}

/** Moyenne des parties d’équipe sur une manche. */
export function computeTeamRoundPartyAverage(individuals, filters, teamNumber, round) {
	return computeTeamPartyStats(individuals, filters, teamNumber, round)?.average ?? null;
}

export function getTeamRankings(teams, individuals, filters) {
	const grouped = new Map();

	teams
		.filter((row) => sameContext(row, filters) && isEquipesTeamRow(row))
		.forEach((row) => {
			if (!grouped.has(row.teamNumber)) {
				grouped.set(row.teamNumber, {
					teamNumber: row.teamNumber,
					teamName: row.teamName,
					club: row.club,
					rows: [],
				});
			}
			grouped.get(row.teamNumber).rows.push(row);
		});

	return [...grouped.values()]
		.map((team) => {
			const stats = groupTeamRounds(team.rows);
			const partyStats = individuals
				? computeTeamPartyStats(individuals, filters, team.teamNumber)
				: null;
			return {
				...team,
				...stats,
				partyAverage: partyStats?.average ?? null,
				teamPartyCount: partyStats?.totalParties ?? 0,
			};
		})
		.sort((a, b) => {
			if (b.totalQuilles !== a.totalQuilles) return b.totalQuilles - a.totalQuilles;
			return a.teamName.localeCompare(b.teamName, 'fr');
		})
		.map((team, index) => ({
			rank: index + 1,
			teamNumber: team.teamNumber,
			teamName: team.teamName,
			club: team.club,
			zone: filters.zone,
			championship: filters.championship,
			category: filters.category,
			quillesN1: team.quillesN1,
			quillesN: team.quillesN,
			totalParties: team.teamPartyCount || team.totalParties,
			totalQuilles: team.totalQuilles,
			totalAverage: team.partyAverage ?? team.totalAverage,
			rounds: team.rounds,
		}));
}

/** Joueurs ayant joué la manche, triés par nom (ordre carton = fiche équipe ailleurs). */
export function getPlayersForTeamRound(players, roundId) {
	return players
		.map((player) => {
			const pr = player.rounds.find((r) => r.round === roundId);
			if (!pr) return null;
			return {
				licence: player.licence,
				playerName: player.playerName,
				quilles: pr.quilles,
				parties: pr.parties,
				quilles1: pr.quilles1,
				quilles2: pr.quilles2,
				quilles3: pr.quilles3,
				quilles4: pr.quilles4,
			};
		})
		.filter(Boolean)
		.sort((a, b) => a.playerName.localeCompare(b.playerName, 'fr'));
}

/** Manches où au moins un joueur de l’équipe a une ligne individuelle. */
export function getTeamPlayedRounds(team) {
	const played = new Set();
	(team.players ?? []).forEach((player) => {
		player.rounds.forEach((r) => played.add(r.round));
	});
	return (team.rounds ?? [])
		.filter((r) => played.has(r.round))
		.sort((a, b) => a.round.localeCompare(b.round, undefined, { numeric: true }));
}

export function maxPartySlotsForRound(roundPlayers) {
	let slots = 0;
	roundPlayers.forEach((p) => {
		if (Number(p.quilles1) > 0) slots = Math.max(slots, 1);
		if (Number(p.quilles2) > 0) slots = Math.max(slots, 2);
		if (Number(p.quilles3) > 0) slots = Math.max(slots, 3);
		if (Number(p.quilles4) > 0) slots = Math.max(slots, 4);
	});
	return slots || 3;
}

/** Score aveugle équipe : total manche CSV − (P1+P2+P3+P4 agrégés joueurs). */
export function computeTeamBlindScore(round) {
	const total = Number(round?.quilles ?? 0);
	const partiesSum = ['quilles1', 'quilles2', 'quilles3', 'quilles4'].reduce(
		(sum, key) => sum + Number(round?.[key] ?? 0),
		0,
	);
	const blind = total - partiesSum;
	return blind > 0 ? blind : 0;
}

export function getTeamDetail(teams, individuals, filters, teamNumber) {
	const teamRows = teams.filter(
		(row) => sameContext(row, filters)
			&& row.teamNumber === teamNumber
			&& isEquipesTeamRow(row),
	);
	if (!teamRows.length) return null;

	const first = teamRows[0];
	const stats = groupTeamRounds(teamRows);

	const playersMap = new Map();
	individuals
		.filter(
			(row) => sameContext(row, filters)
				&& row.teamNumber === teamNumber,
		)
		.forEach((row) => {
			if (!playersMap.has(row.licence)) {
				playersMap.set(row.licence, {
					licence: row.licence,
					playerName: row.playerName,
					club: row.club,
					licenceLevel: row.licenceLevel,
					licenceCategory: row.licenceCategory,
					rounds: [],
				});
			}
			playersMap.get(row.licence).rounds.push({
				round: row.round,
				quilles: Number(row.quilles),
				parties: Number(row.parties),
				average: Number(row.average),
				quilles1: Number(row.quilles1),
				quilles2: Number(row.quilles2),
				quilles3: Number(row.quilles3),
				quilles4: Number(row.quilles4),
				cartonOrder: parseCartonOrder(row.cartonOrder),
				sourceLineIndex: row._sourceLineIndex ?? null,
			});
		});

	const players = [...playersMap.values()]
		.map((player) => {
			const sortedRounds = player.rounds.sort(
				(a, b) => a.round.localeCompare(b.round, undefined, { numeric: true }),
			);
			return {
				...player,
				rounds: sortedRounds,
				...totalsFromRoundRows(sortedRounds),
			};
		})
		.sort((a, b) => a.playerName.localeCompare(b.playerName, 'fr'));

	const rounds = stats.rounds.map((round) => {
		const parties = { quilles1: 0, quilles2: 0, quilles3: 0, quilles4: 0 };
		players.forEach((player) => {
			const playerRound = player.rounds.find((r) => r.round === round.round);
			if (!playerRound) return;
			parties.quilles1 += Number(playerRound.quilles1 ?? 0);
			parties.quilles2 += Number(playerRound.quilles2 ?? 0);
			parties.quilles3 += Number(playerRound.quilles3 ?? 0);
			parties.quilles4 += Number(playerRound.quilles4 ?? 0);
		});
		return { ...round, ...parties };
	});

	return {
		teamNumber: first.teamNumber,
		teamName: first.teamName,
		club: first.club,
		zone: first.zone,
		championship: first.championship,
		category: first.category,
		...stats,
		rounds,
		players,
	};
}

export function getPlayerDetail(individuals, licence) {
	const rows = individuals.filter((row) => row.licence === licence);
	if (!rows.length) return null;

	const first = rows[0];
	const teamNames = [...new Set(rows.map((row) => row.teamName))]
		.sort((a, b) => a.localeCompare(b, 'fr'))
		.join(', ');

	const rounds = rows
		.map((row) => ({
			round: row.round,
			championship: row.championship,
			category: row.category,
			teamNumber: row.teamNumber,
			teamName: row.teamName,
			zone: row.zone,
			quilles: Number(row.quilles),
			parties: Number(row.parties),
			average: Number(row.average),
			quilles1: Number(row.quilles1),
			quilles2: Number(row.quilles2),
			quilles3: Number(row.quilles3),
			quilles4: Number(row.quilles4),
		}))
		.sort((a, b) => {
			const byRound = a.round.localeCompare(b.round, undefined, { numeric: true });
			if (byRound !== 0) return byRound;
			return a.teamName.localeCompare(b.teamName, 'fr');
		});
	const totalQuilles = rounds.reduce((sum, round) => sum + Number(round.quilles || 0), 0);
	const totalParties = rounds.reduce((sum, round) => sum + Number(round.parties || 0), 0);
	const totalAverage = totalParties > 0 ? totalQuilles / totalParties : 0;

	return {
		licence: first.licence,
		playerName: first.playerName,
		club: first.club,
		teamNames,
		licenceLevel: first.licenceLevel,
		licenceCategory: first.licenceCategory,
		totalQuilles,
		totalParties,
		totalAverage,
		rounds,
	};
}

export function formatNumber(value, decimals = 0) {
	if (!Number.isFinite(value)) return '—';
	if (decimals === 0) return String(Math.round(value));
	return value.toLocaleString('fr-FR', {
		minimumFractionDigits: decimals,
		maximumFractionDigits: decimals,
	});
}

/** Manches disponibles pour un contexte championnat (ex. 01, 02…). */
export function getAvailableRounds(teams, filters, individuals = null) {
	const { zone, championship } = filters;
	if (isIndividuelChampionship(teams, zone, championship, individuals) && individuals) {
		return [...new Set(
			individuals
				.filter((row) => sameContext(row, filters))
				.map((row) => row.round)
				.filter(Boolean),
		)].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
	}

	return [...new Set(
		teams
			.filter((row) => sameContext(row, filters) && isEquipesTeamRow(row))
			.map((row) => row.round)
			.filter(Boolean),
	)].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
}

/** Classement équipes sur une manche (quilles CSV équipes, moyenne calculée par partie). */
export function getTeamRankingsForRound(teams, individuals, filters, round) {
	const grouped = new Map();

	teams
		.filter((row) => sameContext(row, filters) && row.round === round && isEquipesTeamRow(row))
		.forEach((row) => {
			if (!grouped.has(row.teamNumber)) {
				const quilles = Number(row.quilles);
				const parties = Number(row.parties);
				const partyStats = individuals
					? computeTeamPartyStats(individuals, filters, row.teamNumber, round)
					: null;
				grouped.set(row.teamNumber, {
					teamNumber: row.teamNumber,
					teamName: row.teamName,
					club: row.club,
					quilles,
					parties: partyStats?.totalParties ?? parties,
					average: partyStats?.average ?? Number(row.average),
				});
			}
		});

	return [...grouped.values()]
		.sort((a, b) => {
			if (b.quilles !== a.quilles) return b.quilles - a.quilles;
			return a.teamName.localeCompare(b.teamName, 'fr');
		})
		.map((team, index) => ({
			...team,
			rank: index + 1,
			zone: filters.zone,
			championship: filters.championship,
			category: filters.category,
		}));
}

/** Top joueurs (moyenne ou quilles sur une manche ou cumul). */
export function getTopPlayers(
	individuals,
	filters,
	{ round = null, limit = 10, allZones = false, equalManches = false } = {},
) {
	const grouped = new Map();

	individuals
		.filter((row) => matchPlayerContext(row, filters, { allZones }) && (!round || row.round === round))
		.forEach((row) => {
			const key = row.licence;
			if (!grouped.has(key)) {
				grouped.set(key, {
					licence: row.licence,
					playerName: row.playerName,
					club: row.club,
					zone: row.zone,
					licenceLevel: row.licenceLevel,
					licenceCategory: row.licenceCategory,
					teamName: row.teamName,
					teamNumber: row.teamNumber,
					manches: 0,
					quilles: 0,
					parties: 0,
				});
			}
			const entry = grouped.get(key);
			entry.manches += 1;
			entry.quilles += Number(row.quilles);
			entry.parties += Number(row.parties);
		});

	let sorted = [...grouped.values()]
		.map((p) => ({
			...p,
			average: p.parties > 0 ? p.quilles / p.parties : 0,
		}))
		.sort((a, b) => {
			if (round) {
				if (b.quilles !== a.quilles) return b.quilles - a.quilles;
			} else if (b.average !== a.average) return b.average - a.average;
			if (a.zone !== b.zone) return a.zone.localeCompare(b.zone, 'fr');
			return a.playerName.localeCompare(b.playerName, 'fr');
		});

	if (equalManches && !round && sorted.length > 0) {
		const maxManches = Math.max(...sorted.map((p) => p.manches));
		sorted = sorted.filter((p) => p.manches === maxManches);
	}

	if (limit == null) return sorted;
	return sorted.slice(0, limit);
}

/** Recherche joueur par nom ou numéro de licence. */
export function searchPlayers(individuals, query, limit = 15) {
	const q = query.trim().toLowerCase().replace(/\s+/g, ' ');
	if (!q || q.length < 2) return [];

	const seen = new Map();
	individuals.forEach((row) => {
		const licence = row.licence ?? '';
		const name = (row.playerName ?? '').toLowerCase();
		const licenceNorm = licence.replace(/^0+/, '');
		const qNorm = q.replace(/^0+/, '');
		const match = name.includes(q)
			|| licence.includes(q)
			|| licenceNorm.includes(qNorm);
		if (!match || seen.has(licence)) return;
		seen.set(licence, {
			licence,
			playerName: row.playerName,
			club: row.club,
			teamName: row.teamName,
			teamNumber: row.teamNumber,
			zone: row.zone,
			championship: row.championship,
			category: row.category,
			totalAverage: Number(row.totalAverage),
		});
	});

	return [...seen.values()]
		.sort((a, b) => a.playerName.localeCompare(b.playerName, 'fr'))
		.slice(0, limit);
}

/** Clubs disponibles (optionnellement filtrés par catégorie). */
export function getClubs(teams, { zone, championship, category } = {}) {
	return [...new Set(
		teams
			.filter((row) => {
				if (!isEquipesTeamRow(row)) return false;
				if (zone && row.zone !== zone) return false;
				if (championship && row.championship !== championship) return false;
				if (category && row.category !== category) return false;
				return Boolean(row.club?.trim());
			})
			.map((row) => row.club),
	)].sort((a, b) => a.localeCompare(b, 'fr'));
}

/** Catégories distinctes sur une saison (toutes zones). */
export function getAllCategories(teams, zone = '') {
	return [...new Set(
		teams
			.filter((row) => isEquipesTeamRow(row) && (!zone || row.zone === zone))
			.map((row) => row.category)
			.filter(Boolean),
	)].sort((a, b) => a.localeCompare(b, 'fr'));
}

/** Classement équipes filtré par championnat et/ou catégorie (+ club), trié par moyenne/partie. */
export function getTeamRankingsByCategory(teams, {
	zone,
	championship = '',
	category = '',
	club = '',
}) {
	const grouped = new Map();

	teams
		.filter((row) => {
			if (!isEquipesTeamRow(row)) return false;
			if (zone && row.zone !== zone) return false;
			if (championship && row.championship !== championship) return false;
			if (category && row.category !== category) return false;
			if (club && row.club !== club) return false;
			return true;
		})
		.forEach((row) => {
			const key = `${row.teamNumber}::${row.championship}::${row.category}`;
			if (!grouped.has(key)) {
				grouped.set(key, {
					teamNumber: row.teamNumber,
					teamName: row.teamName,
					club: row.club,
					championship: row.championship,
					category: row.category,
					zone: row.zone,
					rows: [],
				});
			}
			grouped.get(key).rows.push(row);
		});

	return [...grouped.values()]
		.map((team) => {
			const stats = groupTeamRounds(team.rows);
			return { ...team, ...stats };
		})
		.sort((a, b) => {
			if (b.totalAverage !== a.totalAverage) return b.totalAverage - a.totalAverage;
			if (b.totalQuilles !== a.totalQuilles) return b.totalQuilles - a.totalQuilles;
			const byChamp = a.championship.localeCompare(b.championship, 'fr');
			if (byChamp !== 0) return byChamp;
			const byCategory = a.category.localeCompare(b.category, 'fr');
			if (byCategory !== 0) return byCategory;
			return a.teamName.localeCompare(b.teamName, 'fr');
		})
		.map((team, index) => ({
			rank: index + 1,
			teamNumber: team.teamNumber,
			teamName: team.teamName,
			club: team.club,
			championship: team.championship,
			category: team.category,
			zone: team.zone,
			quillesN1: team.quillesN1,
			quillesN: team.quillesN,
			totalParties: team.totalParties,
			totalQuilles: team.totalQuilles,
			totalAverage: team.totalAverage,
			rounds: team.rounds,
		}));
}

export async function loadAllIndividuals() {
	const years = getAvailableYears();
	const entries = await Promise.all(
		years.map(async (year) => {
			const data = await loadCsvData(year);
			return [year, data.individuals];
		}),
	);
	return Object.fromEntries(entries);
}

export async function loadAllCareerData() {
	const years = getAvailableYears();
	const entries = await Promise.all(
		years.map(async (year) => {
			const data = await loadCsvData(year);
			return [year, {
				individuals: data.individuals,
				teams: data.teams,
			}];
		}),
	);
	return Object.fromEntries(entries);
}

function aggregateIndividualsByLicence(individuals) {
	const grouped = new Map();
	individuals.forEach((row) => {
		const key = row.licence;
		if (!grouped.has(key)) {
			grouped.set(key, {
				licence: row.licence,
				playerName: row.playerName,
				club: row.club,
				licenceLevel: row.licenceLevel,
				licenceCategory: row.licenceCategory,
				teamName: row.teamName,
				manches: 0,
				quilles: 0,
				parties: 0,
				clubs: new Set(),
				licenceLevels: new Set(),
				licenceCategories: new Set(),
			});
		}
		const entry = grouped.get(key);
		entry.manches += 1;
		entry.quilles += Number(row.quilles);
		entry.parties += Number(row.parties);
		if (row.club) entry.clubs.add(row.club);
		if (row.licenceLevel) entry.licenceLevels.add(row.licenceLevel);
		if (row.licenceCategory) entry.licenceCategories.add(row.licenceCategory);
	});
	return grouped;
}

export function buildCareerPlayerCatalog(individualsByYear) {
	if (!individualsByYear) return [];

	const byLicence = new Map();
	const years = Object.keys(individualsByYear)
		.map(Number)
		.sort((a, b) => a - b);

	years.forEach((year) => {
		const seasonPlayers = aggregateIndividualsByLicence(individualsByYear[year] ?? []);
		seasonPlayers.forEach((seasonEntry, licence) => {
			if (!byLicence.has(licence)) {
				byLicence.set(licence, {
					licence,
					playerName: seasonEntry.playerName,
					club: seasonEntry.club,
					licenceLevel: seasonEntry.licenceLevel,
					licenceCategory: seasonEntry.licenceCategory,
					teamName: seasonEntry.teamName,
					manches: 0,
					quilles: 0,
					parties: 0,
					seasons: 0,
					firstYear: year,
					lastYear: year,
					clubs: new Set(),
					licenceLevels: new Set(),
					licenceCategories: new Set(),
				});
			}
			const entry = byLicence.get(licence);
			entry.manches += seasonEntry.manches;
			entry.quilles += seasonEntry.quilles;
			entry.parties += seasonEntry.parties;
			entry.seasons += 1;
			entry.firstYear = Math.min(entry.firstYear, year);
			if (year >= entry.lastYear) {
				entry.lastYear = year;
				entry.playerName = seasonEntry.playerName;
				entry.club = seasonEntry.club;
				entry.licenceLevel = seasonEntry.licenceLevel;
				entry.licenceCategory = seasonEntry.licenceCategory;
				entry.teamName = seasonEntry.teamName;
			}
			seasonEntry.clubs.forEach((club) => entry.clubs.add(club));
			seasonEntry.licenceLevels.forEach((level) => entry.licenceLevels.add(level));
			seasonEntry.licenceCategories.forEach((cat) => entry.licenceCategories.add(cat));
		});
	});

	return [...byLicence.values()].map((entry) => ({
		licence: entry.licence,
		playerName: entry.playerName,
		club: entry.club,
		licenceLevel: entry.licenceLevel,
		licenceCategory: entry.licenceCategory,
		teamName: entry.teamName,
		manches: entry.manches,
		quilles: entry.quilles,
		parties: entry.parties,
		seasons: entry.seasons,
		firstYear: entry.firstYear,
		lastYear: entry.lastYear,
		average: entry.parties > 0 ? entry.quilles / entry.parties : 0,
		clubs: [...entry.clubs],
		licenceLevels: [...entry.licenceLevels],
		licenceCategories: [...entry.licenceCategories],
	}));
}

function aggregateSeasonContexts(rows, individuals, teams, licence) {
	const contexts = new Map();
	const playerRankCache = new Map();
	const teamRankCache = new Map();

	rows.forEach((row) => {
		const key = `${row.zone}::${row.championship}::${row.category}::${row.teamNumber}::${row.teamName}`;
		if (!contexts.has(key)) {
			contexts.set(key, {
				zone: row.zone,
				championship: row.championship,
				category: row.category,
				teamNumber: row.teamNumber,
				teamName: row.teamName,
				manches: 0,
				quilles: 0,
				parties: 0,
			});
		}
		const ctx = contexts.get(key);
		ctx.manches += 1;
		ctx.quilles += Number(row.quilles);
		ctx.parties += Number(row.parties);
	});

	return [...contexts.values()]
		.map((ctx) => {
			const filters = {
				zone: ctx.zone,
				championship: ctx.championship,
				category: ctx.category,
			};
			const rankKey = `${ctx.zone}::${ctx.championship}::${ctx.category}`;
			const isIndividual = getCompetitionType(
				teams,
				ctx.zone,
				ctx.championship,
				individuals,
			) === 'INDIVIDUEL';

			let rank = null;
			let totalParticipants = 0;
			const rankKind = isIndividual ? 'individual' : 'team';

			if (isIndividual) {
				if (!playerRankCache.has(rankKey)) {
					playerRankCache.set(
						rankKey,
						getTopPlayers(individuals, filters, { limit: null }),
					);
				}
				const rankings = playerRankCache.get(rankKey);
				totalParticipants = rankings.length;
				const index = rankings.findIndex((player) => player.licence === licence);
				rank = index >= 0 ? index + 1 : null;
			} else {
				if (!teamRankCache.has(rankKey)) {
					teamRankCache.set(rankKey, getTeamRankings(teams, individuals, filters));
				}
				const rankings = teamRankCache.get(rankKey);
				totalParticipants = rankings.length;
				const teamEntry = rankings.find(
					(entry) => String(entry.teamNumber) === String(ctx.teamNumber),
				);
				rank = teamEntry?.rank ?? null;
			}

			return {
				...ctx,
				average: ctx.parties > 0 ? ctx.quilles / ctx.parties : 0,
				rank,
				totalParticipants,
				rankKind,
			};
		})
		.sort((a, b) => {
			if (a.rank !== b.rank) {
				if (a.rank == null) return 1;
				if (b.rank == null) return -1;
				return a.rank - b.rank;
			}
			const byChamp = a.championship.localeCompare(b.championship, 'fr');
			if (byChamp !== 0) return byChamp;
			return a.category.localeCompare(b.category, 'fr');
		});
}

export function getPlayerCareer(seasonDataByYear, licence) {
	if (!seasonDataByYear || !licence) return null;

	const seasons = [];
	let playerName = '';
	let club = '';
	let licenceLevel = '';
	let licenceCategory = '';
	let lastYear = 0;

	Object.entries(seasonDataByYear).forEach(([yearKey, seasonData]) => {
		const individuals = seasonData?.individuals ?? seasonData;
		const teams = seasonData?.teams ?? [];
		const year = Number(yearKey);
		const rows = individuals.filter((row) => row.licence === licence);
		if (!rows.length) return;

		const first = rows[0];
		if (year >= lastYear) {
			playerName = first.playerName;
			club = first.club;
			licenceLevel = first.licenceLevel;
			licenceCategory = first.licenceCategory;
			lastYear = year;
		}

		const manches = rows.length;
		const quilles = rows.reduce((sum, row) => sum + Number(row.quilles), 0);
		const parties = rows.reduce((sum, row) => sum + Number(row.parties), 0);
		const contexts = aggregateSeasonContexts(rows, individuals, teams, licence);
		const seasonClub = [...new Set(rows.map((row) => row.club).filter(Boolean))]
			.sort((a, b) => a.localeCompare(b, 'fr'))
			.join(', ');
		const licenceCategories = [...new Set(
			rows.map((row) => String(row.licenceCategory ?? '').trim()).filter(Boolean),
		)].sort((a, b) => a.localeCompare(b, 'fr'));

		seasons.push({
			year,
			club: seasonClub,
			manches,
			quilles,
			parties,
			average: parties > 0 ? quilles / parties : 0,
			contextCount: contexts.length,
			contexts,
			licenceCategories,
			hasSeniorLicence: licenceCategories.some((cat) => isSeniorLicenceCategory(cat)),
		});
	});

	if (!seasons.length) return null;

	seasons.sort((a, b) => b.year - a.year);
	const totalManches = seasons.reduce((sum, s) => sum + s.manches, 0);
	const totalQuilles = seasons.reduce((sum, s) => sum + s.quilles, 0);
	const totalParties = seasons.reduce((sum, s) => sum + s.parties, 0);

	return {
		licence,
		playerName,
		club,
		licenceLevel,
		licenceCategory,
		seasons,
		seasonCount: seasons.length,
		firstYear: seasons[seasons.length - 1].year,
		lastYear: seasons[0].year,
		totalManches,
		totalQuilles,
		totalParties,
		totalAverage: totalParties > 0 ? totalQuilles / totalParties : 0,
	};
}

/**
 * Filtre une carrière agrégée (plage d'années et/ou saisons seniors SH/SF/SV/VH).
 * @param {object} career
 * @param {{ fromYear?: number|null, toYear?: number|null, seniorsOnly?: boolean }} options
 */
export function filterPlayerCareer(career, options = {}) {
	if (!career) return null;
	const fromYear = options.fromYear ? Number(options.fromYear) : null;
	const toYear = options.toYear ? Number(options.toYear) : null;
	const seniorsOnly = Boolean(options.seniorsOnly);

	const seasons = (career.seasons ?? []).filter((season) => {
		if (seniorsOnly && !season.hasSeniorLicence) return false;
		if (fromYear != null && !Number.isNaN(fromYear) && season.year < fromYear) return false;
		if (toYear != null && !Number.isNaN(toYear) && season.year > toYear) return false;
		return true;
	});

	const totalManches = seasons.reduce((sum, s) => sum + s.manches, 0);
	const totalQuilles = seasons.reduce((sum, s) => sum + s.quilles, 0);
	const totalParties = seasons.reduce((sum, s) => sum + s.parties, 0);
	const sortedAsc = [...seasons].sort((a, b) => a.year - b.year);

	return {
		...career,
		seasons,
		seasonCount: seasons.length,
		firstYear: sortedAsc.length ? sortedAsc[0].year : career.firstYear,
		lastYear: sortedAsc.length ? sortedAsc[sortedAsc.length - 1].year : career.lastYear,
		totalManches,
		totalQuilles,
		totalParties,
		totalAverage: totalParties > 0 ? totalQuilles / totalParties : 0,
		filter: {
			fromYear,
			toYear,
			seniorsOnly,
			active: Boolean(seniorsOnly || fromYear || toYear),
		},
	};
}

/** Statistiques détaillées carrière : toutes les manches, toutes saisons. */
export function getPlayerCareerStats(seasonDataByYear, licence) {
	if (!seasonDataByYear || !licence) return null;

	const entries = [];
	Object.entries(seasonDataByYear).forEach(([yearKey, seasonData]) => {
		const individuals = seasonData?.individuals ?? seasonData;
		const year = Number(yearKey);
		individuals
			.filter((row) => row.licence === licence)
			.forEach((row) => entries.push({ row, year }));
	});

	if (!entries.length) return null;

	let playerName = '';
	let club = '';
	let licenceLevel = '';
	let licenceCategory = '';
	let lastYear = 0;
	entries.forEach(({ row, year }) => {
		if (year >= lastYear) {
			playerName = row.playerName;
			club = row.club;
			licenceLevel = row.licenceLevel;
			licenceCategory = row.licenceCategory;
			lastYear = year;
		}
	});

	const rounds = entries
		.map(({ row, year }) => ({
			seasonYear: year,
			round: row.round,
			championship: row.championship,
			category: row.category,
			teamNumber: row.teamNumber,
			teamName: row.teamName,
			zone: row.zone,
			licenceCategory: row.licenceCategory,
			quilles: Number(row.quilles),
			parties: Number(row.parties),
			average: Number(row.average),
			quilles1: Number(row.quilles1),
			quilles2: Number(row.quilles2),
			quilles3: Number(row.quilles3),
			quilles4: Number(row.quilles4),
		}))
		.sort((a, b) => {
			if (b.seasonYear !== a.seasonYear) return b.seasonYear - a.seasonYear;
			const byRound = b.round.localeCompare(a.round, undefined, { numeric: true });
			if (byRound !== 0) return byRound;
			const byChamp = a.championship.localeCompare(b.championship, 'fr');
			if (byChamp !== 0) return byChamp;
			return a.category.localeCompare(b.category, 'fr');
		});

	const totalQuilles = rounds.reduce((sum, round) => sum + round.quilles, 0);
	const totalParties = rounds.reduce((sum, round) => sum + round.parties, 0);
	const seasonCount = new Set(rounds.map((round) => round.seasonYear)).size;

	return {
		licence,
		playerName,
		club,
		licenceLevel,
		licenceCategory,
		seasonCount,
		totalManches: rounds.length,
		totalQuilles,
		totalParties,
		totalAverage: totalParties > 0 ? totalQuilles / totalParties : 0,
		rounds,
		firstYear: Math.min(...rounds.map((round) => round.seasonYear)),
		lastYear: Math.max(...rounds.map((round) => round.seasonYear)),
	};
}

/**
 * Filtre les manches carrière (mêmes options que filterPlayerCareer).
 * @param {object} stats
 * @param {{ fromYear?: number|null, toYear?: number|null, seniorsOnly?: boolean }} options
 */
export function filterPlayerCareerStats(stats, options = {}) {
	if (!stats) return null;
	const fromYear = options.fromYear ? Number(options.fromYear) : null;
	const toYear = options.toYear ? Number(options.toYear) : null;
	const seniorsOnly = Boolean(options.seniorsOnly);

	const rounds = (stats.rounds ?? []).filter((round) => {
		if (seniorsOnly && !isSeniorLicenceCategory(round.licenceCategory)) return false;
		if (fromYear != null && !Number.isNaN(fromYear) && round.seasonYear < fromYear) return false;
		if (toYear != null && !Number.isNaN(toYear) && round.seasonYear > toYear) return false;
		return true;
	});

	const totalQuilles = rounds.reduce((sum, round) => sum + round.quilles, 0);
	const totalParties = rounds.reduce((sum, round) => sum + round.parties, 0);
	const years = rounds.map((round) => round.seasonYear);

	return {
		...stats,
		rounds,
		seasonCount: new Set(years).size,
		totalManches: rounds.length,
		totalQuilles,
		totalParties,
		totalAverage: totalParties > 0 ? totalQuilles / totalParties : 0,
		firstYear: years.length ? Math.min(...years) : stats.firstYear,
		lastYear: years.length ? Math.max(...years) : stats.lastYear,
		filter: {
			fromYear,
			toYear,
			seniorsOnly,
			active: Boolean(seniorsOnly || fromYear || toYear),
		},
	};
}
